import { DragDropContext, DropResult } from "@hello-pangea/dnd";
import { Grid } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { SignalRGroup } from "../../../../components";
import { useBreakpoints, useSignalR } from "../../../../hooks";
import { queryKeys, useEditActionItemSection, useEditActionItemSortOrder } from "../../../../http";
import { IPlan } from "../../../../models";
import { lexorank } from "../../../../utilities";
import { Block } from "./Block";
import { EditPlanStructureModal } from "./EditPlanStructureModal";
import { CreateBlockModal } from "./CreateBlockModal";
import { PlanProvider } from "./PlanProvider";

interface IProps {
  teamName: string;
  teamSlug: string;
  plan: IPlan;
  isTableView: boolean;
  isPastView: boolean;
  canEdit?: boolean;
  meetingView?: boolean;
  onActionItemSortOrderChanged: (updatedPlan: IPlan) => void;
  fetchActionItemSubitems: (actionItemGuid: string) => Promise<void>;
}

const Blocks = (props: IProps) => {
  const { mutate: editActionItemSortOrder, isLoading: isUpdatingSortOrder } = useEditActionItemSortOrder();
  const { mutate: editActionItemSection, isLoading: isUpdatingSection } = useEditActionItemSection();
  const [isCreateBlockModalVisible, setIsCreateBlockModalVisible] = useState(false);
  const [isEditPlanStructureModalVisible, setIsEditPlanStructureModalVisible] = useState<boolean>(false);
  const queryClient = useQueryClient();
  const signalR = useSignalR();
  const { isMdUp } = useBreakpoints();

  const hasEditPermission = (props.canEdit && (!isUpdatingSection || !isUpdatingSortOrder)) ?? false;
  const gridSize = props.isTableView || !isMdUp ? 12 : Math.max(Math.min(12 / props.plan.blocks.length, 8), 3);

  useEffect(() => {
    const actionItemGuids =
      props.plan.blocks.flatMap((x) => x.sections.flatMap((y) => y.actionItems.map((z) => z.guid))) ?? [];

    signalR.joinGroup(SignalRGroup.Plan(props.plan.guid));
    signalR.joinGroups(actionItemGuids.map((x) => SignalRGroup.ActionItem(x)));

    return () => {
      signalR.leaveGroup(SignalRGroup.Plan(props.plan.guid));
      signalR.leaveGroups(actionItemGuids.map((x) => SignalRGroup.ActionItem(x)));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.plan]);

  useEffect(() => {
    const handleShouldRefetchPlan = (planGuid: string) => {
      queryClient.refetchQueries(queryKeys.plans.getPlan(planGuid));
      queryClient.refetchQueries(queryKeys.plans.getPlanSubitems(planGuid));
    };
    const handleShouldRefetchActionItem = (actionItemGuid: string) => {
      queryClient.refetchQueries(queryKeys.actionItems.getActionItemSummary(actionItemGuid));
    };

    signalR.onShouldRefetchPlan(handleShouldRefetchPlan);
    signalR.onShouldRefetchActionItem(handleShouldRefetchActionItem);
    return () => {
      signalR.offShouldRefetchActionItem(handleShouldRefetchActionItem);
      signalR.offShouldRefetchPlan(handleShouldRefetchPlan);
    };
  }, [queryClient, signalR]);

  function handleDragEnd(result: DropResult) {
    const { draggableId: actionItemGuid, destination, source } = result;

    // User dragged the item outside of the container
    if (!destination) {
      return;
    }

    // User dragged the item back to where it was
    if (destination?.droppableId === source.droppableId && destination.index === source.index) {
      return;
    }

    let toSection = props.plan.blocks
      .flatMap((block) => block.sections)
      .find((section) => section.guid === destination.droppableId);

    let fromSection = props.plan.blocks
      .flatMap((block) => block.sections)
      .find((section) => section.guid === source.droppableId);

    if (toSection === undefined || fromSection === undefined) {
      return;
    }

    let actionItem = fromSection.actionItems.find((x) => x.guid === actionItemGuid);

    if (actionItem === undefined) {
      return;
    }

    let newSortOrder = "";

    if (fromSection.guid === toSection.guid) {
      const updatedPlan: IPlan = {
        ...props.plan,
        blocks: props.plan.blocks.map((block) => ({
          ...block,
          sections: block.sections.map((section) => {
            if (section.guid === toSection!.guid) {
              return {
                ...section,
                actionItems: [...section.actionItems]
                  .sort((a, b) => a.sortOrder.localeCompare(b.sortOrder))
                  .map((actionItem) => {
                    if (actionItem.guid === actionItemGuid) {
                      newSortOrder = lexorank.getRank(section.actionItems, source.index, destination.index);
                      return {
                        ...actionItem,
                        sortOrder: newSortOrder,
                      };
                    }
                    return actionItem;
                  }),
              };
            }
            return section;
          }),
        })),
      };

      props.onActionItemSortOrderChanged(updatedPlan);

      editActionItemSortOrder({
        actionItemGuid,
        sectionGuid: toSection.guid,
        sortOrder: newSortOrder,
      });
    } else {
      const updatedPlan: IPlan = {
        ...props.plan,
        blocks: props.plan.blocks.map((block) => ({
          ...block,
          sections: block.sections.map((section) => {
            if (section.guid === fromSection!.guid) {
              return {
                ...section,
                actionItems: section.actionItems.filter((actionItem) => actionItem.guid !== actionItemGuid),
              };
            } else if (section.guid === toSection!.guid) {
              newSortOrder = lexorank.getRankAtIndex(section.actionItems, destination.index);
              return {
                ...section,
                actionItems: [
                  ...section.actionItems,
                  {
                    ...actionItem!,
                    sortOrder: newSortOrder,
                  },
                ],
              };
            } else {
              return section;
            }
          }),
        })),
      };

      props.onActionItemSortOrderChanged(updatedPlan);

      editActionItemSection({
        actionItemGuid: result.draggableId,
        fromPlanGuid: props.plan.guid,
        fromSectionGuid: source.droppableId,
        toPlanGuid: props.plan.guid,
        toSectionGuid: destination.droppableId,
        sortOrder: newSortOrder,
      });
    }
  }

  return (
    <PlanProvider planGuid={props.plan.guid} fetchActionItemSubitems={props.fetchActionItemSubitems}>
      <DragDropContext onDragEnd={handleDragEnd}>
        {[...props.plan.blocks]
          .sort((a, b) => a.sortOrder.localeCompare(b.sortOrder))
          .map((block, _) => (
            <Grid size={gridSize} key={block.guid}>
              <Block
                teamName={props.teamName}
                teamSlug={props.teamSlug}
                block={block}
                blocks={props.plan.blocks}
                isTableView={props.isTableView}
                isPastView={props.isPastView}
                canEditPlan={hasEditPermission && !props.isPastView}
                onBlockAddClick={() => setIsCreateBlockModalVisible(true)}
                onEditPlanStructureClicked={() => setIsEditPlanStructureModalVisible(true)}
              />
            </Grid>
          ))}
      </DragDropContext>

      <CreateBlockModal
        visible={isCreateBlockModalVisible}
        blocks={props.plan.blocks}
        onAdded={() => setIsCreateBlockModalVisible(false)}
        onCancelled={() => setIsCreateBlockModalVisible(false)}
      />

      <EditPlanStructureModal
        visible={isEditPlanStructureModalVisible}
        planGuid={props.plan.guid}
        onClose={() => setIsEditPlanStructureModalVisible(false)}
      />
    </PlanProvider>
  );
};

export { Blocks };
